import React, { useEffect, useState } from "react";

import useStyles from "./styles";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { firebase, logFirestoreError } from "../../../firebase";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { FilteredStoresState, StoresState } from "../../../store/stores/types";
import moment from "moment";
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import MaterialTable from "@material-table/core";
import * as userApi from "../../../api/users";
import { AgeVerificationModel } from "../../../models/AgeVerificationModel";
import Alert from "@material-ui/lab/Alert";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { UserState } from "../../../store/users/types";
import { getSingleV2 } from "../../../api/productApi";
import { isOk } from "../../../result";
import { useTheme } from "@material-ui/core/styles";
import { Product } from "../../../models/Product";
import FastfoodIcon from "@material-ui/icons/Fastfood";
import { ShoppingCart } from "@material-ui/icons";
import { useI18n } from "../../../hooks/useI18n";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import {
  thunkFetchStores,
  thunkFetchFilteredStores
} from "../../../store/stores/actions";
import { thunkFetchUsers } from "../../../store/users/actions";

export const AgeVerification = () => {
  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { t } = useI18n();

  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  const usersState = useSelector<AppState, UserState>(s => s.users) || [];
  const filteredStoresState = useSelector<AppState, FilteredStoresState>(
    s => s.filteredStores
  );

  useLoadIfEmpty(!storesState?.stores?.length, thunkFetchStores);
  useLoadIfEmpty(filteredStoresState.isLoading, thunkFetchFilteredStores);
  useLoadIfEmpty(!usersState?.users?.length, thunkFetchUsers);

  const [state, setState] = useState({
    display: true,
    ageVerifications: [] as any,
    selectedAgeVerification: {} as any,
    ageVerificationDetails: {
      user: {} as any,
      products: [] as any,
      isOfMinimumAge: false,
      userAge: 0,
      isLoading: false
    } as any,
    showSuccessMessage: false,
    showErrorMessage: false
  });

  useEffect(() => {
    const storeids = filteredStoresState?.stores
      .filter(s => s.state === "Opened")
      .map(x => x.id);

    if (storeids.length == 0) {
      return;
    }

    const q = query(
      collection(firebase.firestore, "AgeVerification"),
      storeids.length < 10 && storeids.length != 0
        ? where("StoreId", "in", storeids)
        : where("TenantId", "==", filteredStoresState.stores[0].cluster.id),
      where("Verified", "==", false),
      orderBy("Created", "desc")
    );

    let init = true;
    let ageVerifications = [] as any;
    const unsubscribe = onSnapshot(
      q,
      snapShot => {
        snapShot.docChanges().forEach(change => {
          const data = change.doc.data();
          if (storeids.includes(data.StoreId)) {
            let verification = { ...data, id: change.doc.id };
            if (init) {
              ageVerifications.push(verification);
            } else {
              const index = ageVerifications.findIndex(
                (x: any) => x.id === verification.id
              );
              if (index !== -1) ageVerifications.splice(index, 1);
              if (change.type === "added" || change.type === "modified")
                ageVerifications.unshift(verification);
            }
          }
        });

        if (init) init = false;
        setState({ ...state, ageVerifications: ageVerifications });
      },
      logFirestoreError("AgeVerification")
    );

    return () => {
      unsubscribe();
    };
  }, [filteredStoresState]);

  useEffect(() => {
    if (params?.ageVerificationId && state.ageVerifications.length > 0) {
      const verification = state.ageVerifications.find(
        (x: any) => x.id == params.ageVerificationId
      );
      setState({ ...state, selectedAgeVerification: verification });
    }
  }, [state.ageVerifications, params]);

  useEffect(() => {
    const getAgeVerificationDetails = async () => {
      const result = await userApi.getById(
        state.selectedAgeVerification.UserId
      );

      let ageVerificationDetails = { user: {} as any, products: [] as any };
      if (result.type === "SUCCESS") ageVerificationDetails.user = result.data;

      for (const productId of state.selectedAgeVerification.ProductIds) {
        const productResult = await getSingleV2(productId);
        if (isOk(productResult)) {
          ageVerificationDetails.products.push(productResult.value);
        }
      }

      const age = ageVerificationDetails.user.birthDate
        ? moment().diff(moment(ageVerificationDetails.user.birthDate), "years")
        : 0;

      setState({
        ...state,
        ageVerificationDetails: {
          user: ageVerificationDetails.user,
          products: ageVerificationDetails.products,
          userAge: age,
          isOfMinimumAge:
            age >=
            Math.max(
              ...ageVerificationDetails.products.map((p: any) => p.minimumAge)
            ),
          isLoading: false
        }
      });
    };

    if (
      state.selectedAgeVerification &&
      state.selectedAgeVerification?.UserId
    ) {
      setState({
        ...state,
        ageVerificationDetails: {
          ...state.ageVerificationDetails,
          isLoading: true
        }
      });
      getAgeVerificationDetails();
    }
  }, [state.selectedAgeVerification]);

  return (
    <>
      <Dialog
        open={state.display}
        fullScreen={mobile}
        fullWidth={true}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle
          disableTypography
          style={{ display: "flex", alignItems: "center" }}
        >
          <Avatar
            style={{
              backgroundColor: theme.palette.primary.main,
              display: "inline-flex",
              marginRight: 10
            }}
          >
            <ShoppingCart />
          </Avatar>
          <Typography variant={"h6"} display={"inline"}>
            Purchase verifications
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                display: false
              });

              if (location.state) navigate(-1);
              else navigate("..");
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <MaterialTable
            title=""
            isLoading={storesState.isLoading || usersState.isLoading}
            columns={[
              {
                title: "Timestamp",
                field: "Created",
                type: "datetime",
                render: (row: any) =>
                  moment(row.Created.toDate()).format("YYYY-MM-DD HH:mm")
              },
              {
                title: "Store",
                field: "StoreName"
              },
              {
                title: "Name",
                field: "UserName",
                render: (row: any) =>
                  row.FirstName === undefined
                    ? ""
                    : `${row.FirstName} ${row.LastName}`
              },
              {
                title: "Email",
                field: "Email"
              }
            ]}
            data={
              state?.ageVerifications
                ? (state.ageVerifications.map((x: any) => {
                    const user = usersState.users.find(u => u.id === x.UserId);
                    return {
                      ...x,
                      StoreName: storesState.stores.find(
                        s => s.id === x.StoreId
                      )?.name,
                      FirstName: user?.firstName,
                      LastName: user?.lastName,
                      Email: user?.email
                    };
                  }) as [])
                : []
            }
            onRowClick={(evt, row) =>
              setState({ ...state, selectedAgeVerification: row })
            }
            components={{
              Container: props => <Paper {...props} elevation={0} />
            }}
            options={{
              emptyRowsWhenPaging: true,
              search: true,
              pageSizeOptions: [10, 20, 50, 100]
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={
          !!(
            state.selectedAgeVerification &&
            state.selectedAgeVerification?.UserId
          )
        }
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle
          disableTypography
          style={{ display: "flex", alignItems: "center" }}
        >
          <Avatar
            style={{
              backgroundColor: theme.palette.primary.main,
              display: "inline-flex",
              marginRight: 10
            }}
          >
            <ShoppingCart />
          </Avatar>
          <Typography variant={"h6"} display={"inline"}>
            Verify purchase
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                selectedAgeVerification: {} as any,
                showErrorMessage: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          {state.ageVerificationDetails.isLoading ? (
            <div className={classes.spinnerContainer}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Typography
                variant={"subtitle1"}
                component={"p"}
                style={{ marginTop: 0, marginBottom: 10 }}
              >
                I approve that:{" "}
                <Typography
                  variant={"h6"}
                  style={{
                    fontWeight: "bold",
                    lineHeight: "1.2rem",
                    marginTop: 8
                  }}
                >
                  {state.ageVerificationDetails.user.firstName
                    ? `${state.ageVerificationDetails.user.firstName} ${state.ageVerificationDetails.user.lastName}`
                    : state.ageVerificationDetails.user.email}
                </Typography>
                <Typography
                  variant={"subtitle1"}
                  color={
                    state.ageVerificationDetails.isOfMinimumAge
                      ? "primary"
                      : "secondary"
                  }
                  style={{ fontWeight: "bold" }}
                >
                  Age:{" "}
                  {state.ageVerificationDetails.userAge === 0
                    ? "-"
                    : state.ageVerificationDetails.userAge}
                </Typography>
                is allowed to buy the following products:
              </Typography>
              <List>
                {state.ageVerificationDetails?.products &&
                  state.ageVerificationDetails?.products.map((p: Product) => (
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar
                          variant={"square"}
                          imgProps={{ style: { objectFit: "contain" } }}
                          src={p.imageUrl}
                        >
                          <FastfoodIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={p.name} />
                      {p.minimumAge && (
                        <ListItemAvatar>
                          <Avatar
                            style={{
                              backgroundColor: theme.palette.error.dark,
                              fontSize: "1rem"
                            }}
                          >
                            {`${p.minimumAge}+`}
                          </Avatar>
                        </ListItemAvatar>
                      )}
                    </ListItem>
                  ))}
              </List>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 40
                }}
              >
                <Button
                  variant="contained"
                  onClick={async () => {
                    const result = await userApi.verifyAge({
                      ...state.selectedAgeVerification,
                      Created: state.selectedAgeVerification.Created.toDate(),
                      Updated: state.selectedAgeVerification.Updated.toDate(),
                      Declined: true
                    } as AgeVerificationModel);

                    if (result.ok) {
                      setState({
                        ...state,
                        selectedAgeVerification: {},
                        showSuccessMessage: true,
                        showErrorMessage: false
                      });
                    } else {
                      setState({ ...state, showErrorMessage: true });
                    }
                  }}
                >
                  Decline
                </Button>
                <Button
                  style={{ marginLeft: 15 }}
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const result = await userApi.verifyAge({
                      ...state.selectedAgeVerification,
                      Created: state.selectedAgeVerification.Created.toDate(),
                      Updated: state.selectedAgeVerification.Updated.toDate(),
                      Verified: true
                    } as AgeVerificationModel);

                    if (result.ok) {
                      setState({
                        ...state,
                        selectedAgeVerification: {},
                        showSuccessMessage: true,
                        showErrorMessage: false
                      });
                    } else {
                      setState({ ...state, showErrorMessage: true });
                    }
                  }}
                >
                  Approve
                </Button>
              </div>
              <div>
                {state.showErrorMessage && (
                  <Alert severity="error">
                    {t("common.messages.failedToVerifyPurchase")}
                  </Alert>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={state.showSuccessMessage}
        autoHideDuration={2000}
        onClose={() => {
          setState({ ...state, showSuccessMessage: false });
        }}
      >
        <Alert
          onClose={() => setState({ ...state, showSuccessMessage: false })}
          severity="success"
        >
          Purchase has been successfully verified
        </Alert>
      </Snackbar>
    </>
  );
};
